<template>
  <div class="cashier-page min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50">
    <!-- 装饰性背景 -->
    <div class="fixed inset-0 pointer-events-none overflow-hidden">
      <div class="absolute top-0 left-1/4 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl" />
      <div class="absolute bottom-0 right-1/4 w-96 h-96 bg-emerald-400/10 rounded-full blur-3xl" />
    </div>

    <main class="relative z-10 mx-auto max-w-6xl px-4 py-6 md:px-8 md:py-10">
      <!-- 页面头部 -->
      <header class="mb-6 md:mb-8">
        <div class="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-white/80 bg-white/70 p-5 shadow-lg backdrop-blur-xl md:p-6">
          <div class="flex items-center gap-4">
            <div class="relative flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-600 shadow-lg shadow-blue-500/30">
              <svg class="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
              <div class="absolute -right-1 -top-1 h-3 w-3 rounded-full bg-emerald-400 animate-pulse"></div>
            </div>
            <div>
              <div class="text-xs font-semibold uppercase tracking-widest text-blue-600">Secure Checkout</div>
              <h1 class="text-xl font-bold text-slate-900 md:text-2xl">收银台</h1>
            </div>
          </div>
          <!-- <div class="flex items-center gap-3 rounded-full bg-gradient-to-r from-emerald-50 to-teal-50 px-4 py-2 border border-emerald-200/60 shadow-sm">
            <svg class="h-4 w-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            <span class="text-sm font-medium text-emerald-700">全程加密传输</span>
          </div> -->
        </div>
      </header>

      <!-- 加载状态 -->
      <section v-if="loading" class="flex flex-col items-center justify-center rounded-2xl border border-slate-200/60 bg-white/80 p-12 shadow-xl backdrop-blur">
        <div class="h-12 w-12 animate-spin rounded-full border-4 border-blue-200 border-t-blue-600" />
        <p class="mt-4 text-slate-600">正在加载账单信息...</p>
      </section>

      <!-- 错误状态 -->
      <section v-else-if="loadError" class="rounded-2xl border border-red-200/60 bg-red-50/80 p-8 text-center shadow-lg backdrop-blur">
        <div class="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
          <svg class="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h2 class="mt-4 text-lg font-semibold text-red-700">收银台加载失败</h2>
        <p class="mt-2 text-sm text-red-600">{{ loadError }}</p>
      </section>

      <!-- 主要内容 -->
      <section v-else class="grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
        <!-- 左侧信息卡片 -->
        <div class="space-y-5">
          <!-- 账单信息 -->
          <article class="overflow-hidden rounded-2xl border border-slate-200/60 bg-white shadow-xl transition-shadow hover:shadow-2xl">
            <div class="bg-gradient-to-r from-slate-50 to-blue-50 p-5 md:p-6">
              <div class="flex items-start justify-between gap-4">
                <div>
                  <h2 class="mt-2 text-xl font-bold text-slate-900 md:text-2xl">{{ presentation.title || '-' }}</h2>
                </div>
                <span class="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold shadow-sm" :class="statusClass">
                  <span v-if="statusCode === 0" class="h-1.5 w-1.5 rounded-full bg-current animate-pulse" />
                  {{ statusLabel }}
                </span>
              </div>
            </div>
            <div class="grid gap-4 p-5 md:p-6 sm:grid-cols-[1.2fr_1fr]">
              <div class="rounded-2xl bg-gradient-to-br from-blue-600 via-blue-500 to-indigo-600 p-6 shadow-lg shadow-blue-500/20">
                <div class="flex items-center gap-2 mb-3">
                  <svg class="h-5 w-5 text-blue-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p class="text-sm font-medium text-blue-100">应付金额</p>
                </div>
                <p class="text-4xl font-black text-white tracking-tight">{{ currencyText(presentation.amount) }}</p>
              </div>
              <div class="rounded-2xl bg-gradient-to-br from-slate-50 to-white p-5 border border-slate-200/80">
                <div class="flex items-center gap-2">
                  <svg class="h-4 w-4 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p class="text-xs font-medium text-amber-600">支付参考时限</p>
                </div>
                <p class="mt-2 text-base font-bold text-slate-800">{{ datetimeText(presentation.expiresAt) }}</p>
                <p class="mt-1 text-xs text-slate-500">收银台号：{{ presentation.cashierNo || cashierNo }}</p>
                <p class="mt-2 text-xs text-amber-700">订单不会因超时自动关闭，若支付凭证失效可重新发起。</p>
              </div>
            </div>
          </article>

          <!-- 订单信息 -->
          <article class="overflow-hidden rounded-2xl border border-slate-200/60 bg-white shadow-xl transition-shadow hover:shadow-2xl">
            <div class="flex items-center justify-between gap-3 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white px-5 py-4 md:px-6">
              <div class="flex items-center gap-3">
                <div class="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100">
                  <svg class="h-4 w-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <h3 class="text-base font-bold text-slate-900">订单信息</h3>
              </div>
            </div>
            <div class="p-5 md:p-6">
              <div class="grid gap-3 sm:grid-cols-2">
                <div class="rounded-xl border border-slate-200/80 bg-slate-50/50 p-3.5">
                  <p class="text-xs text-slate-500">订单编号</p>
                  <p class="mt-1 break-all font-mono text-sm font-semibold text-slate-900">{{ orderInfo.orderNo || '-' }}</p>
                </div>
                <div class="rounded-xl border border-slate-200/80 bg-slate-50/50 p-3.5">
                  <p class="text-xs text-slate-500">订单类型</p>
                  <p class="mt-1 text-sm font-semibold text-slate-900">{{ orderInfo.orderType || '-' }}</p>
                </div>
                <div class="rounded-xl border border-slate-200/80 bg-slate-50/50 p-3.5">
                  <p class="text-xs text-slate-500">下单时间</p>
                  <p class="mt-1 text-sm font-semibold text-slate-700">{{ datetimeText(orderInfo.orderCreateTime) }}</p>
                </div>
                <div class="rounded-xl border border-slate-200/80 bg-slate-50/50 p-3.5">
                  <p class="text-xs text-slate-500">购买人</p>
                  <p class="mt-1 text-sm font-semibold text-slate-900">
                    {{ orderInfo.customerName || '-' }}<span v-if="orderInfo.customerPhoneMasked" class="text-slate-500"> · {{ orderInfo.customerPhoneMasked }}</span>
                  </p>
                </div>
              </div>
              <div class="mt-4 rounded-xl border border-dashed border-slate-300/80 bg-gradient-to-r from-slate-50 to-blue-50/30 p-4">
                <div class="flex items-start gap-2">
                  <svg class="mt-0.5 h-4 w-4 flex-shrink-0 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                  <div>
                    <p class="text-xs text-slate-500">商品/服务摘要</p>
                    <p class="mt-1 text-sm font-medium text-slate-800">{{ orderInfo.productSummary || orderInfo.orderTitle || '暂无' }}</p>
                  </div>
                </div>
              </div>
            </div>
          </article>

          <!-- 支付方式选择 -->
          <article class="overflow-hidden rounded-2xl border border-slate-200/60 bg-white shadow-xl transition-shadow hover:shadow-2xl">
            <div class="flex items-center gap-3 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white px-5 py-4 md:px-6">
              <div class="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100">
                <svg class="h-4 w-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
              </div>
              <h3 class="text-base font-bold text-slate-900">选择支付方式</h3>
            </div>
            <div class="p-5 md:p-6">
              <div v-if="paymentMethodCards.length" class="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <button
                  v-for="card in paymentMethodCards"
                  :key="card.key"
                  type="button"
                  class="group relative overflow-hidden rounded-2xl border-2 p-4 text-center transition-all duration-300 hover:scale-[1.02]"
                  :class="activeMethod === card.key ? card.activeClass : card.inactiveClass"
                  :disabled="actionLoading || isTerminal"
                  @click="activateMethod(card.key)"
                >
                  <div class="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl transition-all duration-300" :class="activeMethod === card.key ? card.iconActiveBg : card.iconInactiveBg">
                    <svg v-if="card.icon === 'wechat'" class="h-7 w-7" viewBox="0 0 24 24" fill="currentColor" :class="activeMethod === card.key ? card.iconActiveColor : 'text-slate-600'">
                      <path d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 01.213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 00.167-.054l1.903-1.114a.864.864 0 01.717-.098 10.16 10.16 0 002.837.403c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.348-8.596-6.348zM5.785 5.991c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 01-1.162 1.178A1.17 1.17 0 014.623 7.17c0-.651.52-1.18 1.162-1.18zm5.813 0c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 01-1.162 1.178 1.17 1.17 0 01-1.162-1.178c0-.651.52-1.18 1.162-1.18zm5.34 2.867c-1.797-.052-3.746.512-5.28 1.786-1.72 1.428-2.687 3.72-1.78 6.22.942 2.453 3.666 4.229 6.884 4.229.826 0 1.622-.12 2.361-.336a.722.722 0 01.598.082l1.584.926a.272.272 0 00.14.045c.134 0 .24-.111.24-.247 0-.06-.024-.12-.04-.177l-.327-1.233a.582.582 0 01-.023-.156.49.49 0 01.201-.398C23.024 18.48 24 16.82 24 14.98c0-3.21-2.931-5.837-6.656-6.088V8.87c-.135-.004-.272-.012-.407-.012zm-1.54 3.576c.536 0 .97.44.97.983a.976.976 0 01-.97.983.976.976 0 01-.97-.983c0-.542.434-.983.97-.983zm4.857 0c.536 0 .97.44.97.983a.976.976 0 01-.97.983.976.976 0 01-.97-.983c0-.542.434-.983.97-.983z"/>
                    </svg>
                    <svg v-else-if="card.icon === 'alipay'" class="h-8 w-8" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg">
                      <path d="M492.343 777.511c-67.093 32.018-144.129 51.939-227.552 32.27-83.424-19.678-142.626-73.023-132.453-171.512 10.192-98.496 115.478-132.461 202.07-132.461 86.622 0 250.938 56.122 250.938 56.122s13.807-30.937 27.222-66.307c13.405-35.365 17.21-63.785 17.21-63.785H279.869v-35.067h169.995v-67.087l-211.925 1.526v-44.218h211.925v-100.63h111.304v100.629H788.35v44.218l-227.181 1.524v62.511l187.584 1.526s-3.391 35.067-27.17 98.852c-23.755 63.783-46.061 96.312-46.061 96.312L960 685.279V243.2C960 144.231 879.769 64 780.8 64H243.2C144.231 64 64 144.231 64 243.2v537.6C64 879.769 144.231 960 243.2 960h537.6c82.487 0 151.773-55.806 172.624-131.668L625.21 672.744s-65.782 72.748-132.867 104.767z" :fill="activeMethod === card.key ? 'currentColor' : '#475569'" />
                      <path d="M297.978 559.871c-104.456 6.649-129.974 52.605-129.974 94.891s25.792 101.073 148.548 101.073c122.727 0 226.909-123.77 226.909-123.77s-141.057-78.842-245.483-72.194z" :fill="activeMethod === card.key ? 'currentColor' : '#475569'" />
                    </svg>
                    <svg v-else-if="card.icon === 'ebank'" class="h-6 w-6" :class="activeMethod === card.key ? card.iconActiveColor : 'text-slate-600'" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                    </svg>
                    <svg v-else class="h-6 w-6" :class="activeMethod === card.key ? card.iconActiveColor : 'text-slate-600'" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <p class="mt-3 text-sm font-semibold" :class="activeMethod === card.key ? card.textActiveClass : 'text-slate-700'">{{ card.label }}</p>
                  <p class="mt-1 text-xs" :class="activeMethod === card.key ? card.descActiveClass : 'text-slate-400'">{{ card.description }}</p>
                </button>
              </div>
              <div v-else class="rounded-xl border border-dashed border-slate-300 bg-slate-50/70 px-4 py-6 text-center">
                <p class="text-sm font-medium text-slate-700">当前账单暂未开放可用支付方式</p>
                <p class="mt-2 text-xs text-slate-500">请联系商家确认账单状态或稍后重新进入收银台。</p>
              </div>
            </div>
          </article>
        </div>

        <!-- 右侧操作面板 -->
        <aside class="space-y-5">
          <!-- 支付操作区 -->
          <article class="overflow-hidden rounded-2xl border border-slate-200/60 bg-white shadow-xl transition-shadow hover:shadow-2xl">
            <div class="flex items-center gap-3 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white px-5 py-4">
              <div class="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100">
                <svg class="h-4 w-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 class="text-base font-bold text-slate-900">支付操作区</h3>
            </div>
            <div class="p-5 md:p-6 space-y-4">
              <!-- 扫码支付 -->
              <div v-if="currentScanMethod && allowScan" class="space-y-4">
                <!-- 二维码展示区 -->
                <div class="relative">
                  <!-- 二维码容器 -->
                  <div class="overflow-hidden rounded-2xl border-2 border-dashed" :class="scanExpired ? 'border-amber-400 bg-amber-50/50' : 'border-slate-200 bg-gradient-to-br from-slate-50 to-blue-50/30'">
                    <!-- 有二维码时 -->
                    <div v-if="scanData && scanQrImage" class="p-6">
                      <div class="relative mx-auto w-56 h-56">
                        <img :src="scanQrImage" alt="支付二维码" class="w-full h-full rounded-xl object-contain">
                        <!-- 二维码过期遮罩 -->
                        <div v-if="scanExpired" class="absolute inset-0 flex flex-col items-center justify-center rounded-xl bg-slate-900/80 backdrop-blur-sm">
                          <svg class="h-10 w-10 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <p class="mt-2 text-sm font-medium text-white">二维码已过期</p>
                        </div>
                      </div>
                    </div>
                    <!-- 无二维码时 -->
                    <div v-else class="flex flex-col items-center justify-center py-12 px-6">
                      <div class="flex h-20 w-20 items-center justify-center rounded-2xl bg-slate-100">
                        <svg class="h-10 w-10 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                        </svg>
                      </div>
                      <p class="mt-4 text-sm text-slate-500">点击下方按钮生成支付二维码</p>
                    </div>
                  </div>
                </div>

                <!-- 操作按钮 -->
                <div class="space-y-2">
                  <button v-if="!scanData" class="action-btn" :disabled="actionLoading || isTerminal || !allowScan" @click="createScanPay(activeMethod)">
                    <svg v-if="!actionLoading" class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                    </svg>
                    <span v-if="actionLoading" class="animate-spin">
                      <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </span>
                    {{ actionLoading ? '生成中...' : '生成支付二维码' }}
                  </button>

                  <button v-else-if="scanExpired" class="action-btn" :disabled="actionLoading || isTerminal" @click="refreshCurrentScan">
                    <svg v-if="!actionLoading" class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    <span v-if="actionLoading" class="animate-spin">
                      <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </span>
                    {{ actionLoading ? '刷新中...' : '刷新二维码' }}
                  </button>

                  <!-- 支付链接 -->
                  <div v-if="scanPayUrl" class="rounded-xl border border-slate-200 bg-slate-50 p-3">
                    <div class="flex items-center justify-between gap-2">
                      <p class="flex-1 truncate text-xs text-slate-500">支付链接</p>
                      <button v-if="!isMobileClient" class="shrink-0 text-xs text-blue-600 hover:text-blue-700" @click="copyScanPayUrl">
                        {{ actionLoading ? '复制中...' : '复制' }}
                      </button>
                    </div>
                    <p class="mt-1 break-all font-mono text-xs text-slate-700">{{ scanPayUrl }}</p>
                  </div>
                </div>

                <!-- 支付提示 -->
                <p v-if="scanData" class="text-center text-sm text-slate-600">
                  请使用 <span class="font-semibold" :class="activeMethod === 'ALIPAY' ? 'text-blue-600' : 'text-green-600'">{{ activeMethod === 'ALIPAY' ? '支付宝' : '微信' }}</span> 扫码支付
                </p>
                <p v-if="scanExpired && presentation.voucherExpiresAt" class="text-center text-xs text-amber-600">
                  二维码有效期至：{{ datetimeText(presentation.voucherExpiresAt) }}
                </p>
              </div>

              <!-- 网银支付操作 -->
              <div v-if="activeMethod === 'EBANK' && allowEbank" class="mt-5 space-y-4">
                <!-- 网银支付配置卡片 -->
                <div class="overflow-hidden rounded-2xl border border-indigo-200/60 bg-gradient-to-br from-indigo-50/80 to-white shadow-lg">
                  <!-- 卡片头部 -->
                  <div class="bg-gradient-to-r from-indigo-500 to-purple-500 px-5 py-4">
                    <div class="flex items-center gap-3">
                      <div class="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20 backdrop-blur">
                        <svg class="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                      </div>
                      <div>
                        <p class="font-semibold text-white">网银支付</p>
                        <p class="text-xs text-indigo-100">选择开户行和账户类型</p>
                      </div>
                    </div>
                  </div>

                  <div class="p-5 space-y-5">
                    <!-- 银行选择 -->
                    <div>
                      <label class="mb-2 flex items-center gap-1.5 text-sm font-medium text-slate-700" for="ebank-bank-code">
                        <svg class="h-4 w-4 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5" />
                        </svg>
                        付款银行
                      </label>
                      <div class="relative">
                        <select
                          id="ebank-bank-code"
                          v-model="selectedEbankBankCode"
                          class="peer block w-full cursor-pointer appearance-none rounded-xl border-2 border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-900 outline-none transition-all focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 disabled:cursor-not-allowed disabled:opacity-50"
                          :disabled="actionLoading || isTerminal"
                        >
                          <option v-for="option in ebankOptions" :key="option.value" :value="option.value">
                            {{ option.label }}
                          </option>
                        </select>
                        <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                          <svg class="h-5 w-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    <!-- 账户类型选择 -->
                    <div>
                      <label class="mb-2 block text-sm font-medium text-slate-700">账户类型</label>
                      <div class="flex rounded-xl border-2 border-slate-200 bg-slate-50 p-1 gap-1">
                        <button
                          v-for="option in ebankAccountTypeOptions"
                          :key="option.value"
                          type="button"
                          class="flex-1 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200"
                          :class="selectedEbankAccountType === option.value
                            ? 'bg-white text-indigo-600 shadow-sm'
                            : 'text-slate-500 hover:text-slate-700'"
                          :disabled="actionLoading || isTerminal"
                          @click="selectedEbankAccountType = option.value"
                        >
                          {{ option.label }}
                        </button>
                      </div>
                      <p class="mt-2 text-xs text-slate-500">
                        {{ selectedEbankAccountType === 'PERSONAL' ? '个人网银，走 B2C 银行支付通道' : '企业网银，走 B2B 银行支付通道' }}
                      </p>
                    </div>

                    <!-- 提示信息 -->
                    <div class="flex items-start gap-2.5 rounded-xl bg-amber-50/80 p-3 border border-amber-200/60">
                      <svg class="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <p class="text-xs text-amber-700">发起后将跳转到银行页面完成支付，请勿关闭当前收银台页面。</p>
                    </div>
                  </div>
                </div>

                <!-- 支付按钮 -->
                <button class="action-btn" :disabled="actionLoading || isTerminal || !allowEbank || !selectedEbankBankCode || !selectedEbankAccountType" @click="createEbankPay">
                  <svg v-if="!actionLoading" class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  <span v-if="actionLoading" class="animate-spin">
                    <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </span>
                  {{ actionLoading ? '处理中...' : `前往 ${selectedEbankBank?.label || '网银'} 支付` }}
                </button>
              </div>

              <!-- 大额转账操作 -->
              <div v-if="activeMethod === 'BANK_TRANSFER' && allowTransfer" class="mt-5 space-y-4">
                <button class="action-btn" :disabled="actionLoading || isTerminal || !allowTransfer" @click="createTransferPay">
                  <svg v-if="!actionLoading" class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  <span v-if="actionLoading" class="animate-spin">
                    <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </span>
                  {{ actionLoading ? '处理中...' : '获取转账信息' }}
                </button>
              </div>
            </div>
          </article>

          <!-- 支付结果区 -->
          <article class="overflow-hidden rounded-2xl border border-slate-200/60 bg-white shadow-xl transition-shadow hover:shadow-2xl">
            <div class="border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white px-5 py-4">
              <h3 class="text-base font-bold text-slate-900">支付结果</h3>
            </div>
            <div class="p-5 md:p-6">
              <template v-if="statusCode === 2">
                <div class="flex flex-col items-center text-center">
                  <div class="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
                    <svg class="h-8 w-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 class="mt-4 text-lg font-bold text-emerald-700">支付成功</h3>
                  <p class="mt-2 text-sm text-slate-600">订单已完成支付，请保留截图作为凭证。</p>
                  <div class="mt-4 w-full rounded-xl bg-emerald-50/80 p-4 text-left border border-emerald-200/60">
                    <p class="text-xs text-emerald-700"><span class="font-semibold">支付金额：</span>{{ currencyText(presentation.amount) }}</p>
                    <p class="text-xs text-emerald-700"><span class="font-semibold">账单号：</span>{{ presentation.cashierNo || cashierNo }}</p>
                    <p class="mt-1 text-xs text-emerald-700"><span class="font-semibold">订单号：</span>{{ orderInfo.orderNo || '-' }}</p>
                    <p class="mt-1 text-xs text-emerald-700"><span class="font-semibold">支付方式：</span>{{ successMethodLabel }}</p>
                    <p class="mt-1 text-xs text-emerald-700"><span class="font-semibold">支付时间：</span>{{ datetimeText(presentation.paidTime) }}</p>
                  </div>
                </div>
              </template>

              <template v-else-if="statusCode === 3">
                <div class="flex flex-col items-center text-center">
                  <div class="flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
                    <svg class="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                  <h3 class="mt-4 text-lg font-bold text-red-700">收银台已关闭</h3>
                  <p class="mt-2 text-sm text-slate-600">请联系商家确认账单状态后再继续支付。</p>
                </div>
              </template>

              <template v-else-if="statusCode === 4 && !scanData">
                <div class="flex flex-col items-center text-center">
                  <div class="flex h-16 w-16 items-center justify-center rounded-full bg-amber-100">
                    <svg class="h-8 w-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M10.29 3.86l-7.5 13A1 1 0 003.66 18h16.68a1 1 0 00.87-1.5l-7.5-13a1 1 0 00-1.74 0z" />
                    </svg>
                  </div>
                  <h3 class="mt-4 text-lg font-bold text-amber-700">当前支付凭证已失效</h3>
                  <p class="mt-2 text-sm text-slate-600">{{ presentation.failureReason || '订单仍可继续支付，请重新发起当前支付方式获取新的二维码、网银跳转或转账账号。' }}</p>
                  <p v-if="presentation.voucherExpiresAt" class="mt-2 text-xs text-amber-700">
                    原支付凭证失效时间：{{ datetimeText(presentation.voucherExpiresAt) }}
                  </p>
                </div>
              </template>

              <template v-else>
                <!-- 扫码支付提示（当二维码在操作区显示时） -->
                <div v-if="currentScanMethod && allowScan && !scanData" class="flex flex-col items-center py-6 text-center">
                  <div class="flex h-14 w-14 items-center justify-center rounded-full bg-slate-100">
                    <svg class="h-7 w-7 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </div>
                  <p class="mt-3 text-sm text-slate-500">请在上方生成支付二维码</p>
                </div>

                <div v-else-if="transferData" class="space-y-4 rounded-xl border border-slate-200/80 bg-gradient-to-r from-slate-50 to-blue-50/30 p-4">
                  <p class="flex items-center gap-2 text-sm font-semibold text-slate-900">
                    <svg class="h-5 w-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    转账收款信息
                  </p>
                  <div class="space-y-2 rounded-lg bg-white/80 p-3">
                    <p class="text-sm"><span class="text-slate-500">开户名：</span><span class="font-medium text-slate-900">{{ transferData.accountName || '-' }}</span></p>
                    <p class="text-sm"><span class="text-slate-500">收款账号：</span><span class="font-mono font-medium text-slate-900">{{ transferData.accountNoMasked || '-' }}</span></p>
                    <p class="text-sm"><span class="text-slate-500">开户行：</span><span class="font-medium text-slate-900">{{ transferData.bankName || '-' }}</span></p>
                    <p class="text-sm"><span class="text-slate-500">开户地：</span><span class="font-medium text-slate-900">{{ transferData.bankAddress || '-' }}</span></p>
                    <p class="text-sm"><span class="text-slate-500">分支行：</span><span class="font-medium text-slate-900">{{ transferData.branchBank || '-' }}</span></p>
                    <p class="text-sm"><span class="text-slate-500">凭证有效至：</span><span class="font-medium text-slate-900">{{ datetimeText(transferData.expiresAt) }}</span></p>
                    <p class="text-xs text-amber-700">若该账号已失效，请重新点击"获取转账信息"申请新的收款账号。</p>
                  </div>
                  <button v-if="transferData.accountNo" type="button" class="w-full rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-blue-300 hover:text-blue-600" @click="copyTransferAccountNo">
                    复制收款账号
                  </button>
                </div>

                <div v-else class="flex flex-col items-center py-6 text-center">
                  <div class="flex h-14 w-14 items-center justify-center rounded-full bg-slate-100">
                    <svg class="h-7 w-7 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </div>
                  <p class="mt-3 text-sm text-slate-500">请选择支付方式并发起支付</p>
                </div>
              </template>
            </div>
          </article>
        </aside>
      </section>
    </main>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { ElMessage } from 'element-plus'
import QRCode from 'qrcode'
import {
  createCashierEbank,
  createCashierScan,
  createCashierTransfer,
  getCashierPresentation,
  getCashierStatus
} from '@/api/cashier'

const route = useRoute()
const cashierNo = computed(() => route.params.cashierNo)
const isMobileClient = /android|iphone|ipad|mobile/i.test(navigator.userAgent || '')
const clientType = isMobileClient ? 'MOBILE' : 'PC'

const loading = ref(true)
const loadError = ref('')
const actionLoading = ref(false)
const presentation = ref({})
const orderInfo = ref({})
const activeMethod = ref('WECHAT')
const statusCode = ref(0)
const attemptNo = ref('')
const pollTimer = ref(null)
const selectedEbankBankCode = ref('ICBC')
const selectedEbankAccountType = ref('PERSONAL')
const ebankOptions = [
  { label: '工商银行', value: 'ICBC' },
  { label: '建设银行', value: 'CCB' },
  { label: '农业银行', value: 'ABC' },
  { label: '中国银行', value: 'BOC' },
  { label: '招商银行', value: 'CMB' },
  { label: '浦发银行', value: 'SPDB' },
  { label: '民生银行', value: 'CMBC' },
  { label: '兴业银行', value: 'CIB' },
  { label: '平安银行', value: 'PINGAN' }
]
const ebankAccountTypeOptions = [
  { label: '个人户', value: 'PERSONAL' },
  { label: '公户', value: 'CORPORATE' }
]
const paymentMethodMeta = {
  WECHAT: {
    key: 'WECHAT',
    icon: 'wechat',
    label: '微信支付',
    activeClass: 'border-green-500 bg-gradient-to-br from-green-50 to-emerald-50 shadow-lg shadow-green-500/25 ring-2 ring-green-500/20',
    inactiveClass: 'border-slate-200 bg-white hover:border-green-300 hover:shadow-md',
    iconActiveBg: 'bg-gradient-to-br from-green-500 to-emerald-500 shadow-lg shadow-green-500/30',
    iconInactiveBg: 'bg-slate-100 group-hover:bg-green-100',
    iconActiveColor: 'text-white',
    textActiveClass: 'text-green-700',
    descActiveClass: 'text-green-600'
  },
  ALIPAY: {
    key: 'ALIPAY',
    icon: 'alipay',
    label: '支付宝',
    activeClass: 'border-blue-500 bg-gradient-to-br from-blue-50 to-cyan-50 shadow-lg shadow-blue-500/25 ring-2 ring-blue-500/20',
    inactiveClass: 'border-slate-200 bg-white hover:border-blue-300 hover:shadow-md',
    iconActiveBg: 'bg-gradient-to-br from-blue-500 to-cyan-500 shadow-lg shadow-blue-500/30',
    iconInactiveBg: 'bg-slate-100 group-hover:bg-blue-100',
    iconActiveColor: 'text-white',
    textActiveClass: 'text-blue-700',
    descActiveClass: 'text-blue-600'
  },
  EBANK: {
    key: 'EBANK',
    icon: 'ebank',
    label: '网银支付',
    activeClass: 'border-indigo-500 bg-gradient-to-br from-indigo-50 to-purple-50 shadow-lg shadow-indigo-500/25 ring-2 ring-indigo-500/20',
    inactiveClass: 'border-slate-200 bg-white hover:border-indigo-300 hover:shadow-md',
    iconActiveBg: 'bg-gradient-to-br from-indigo-500 to-purple-500 shadow-lg shadow-indigo-500/30',
    iconInactiveBg: 'bg-slate-100 group-hover:bg-indigo-100',
    iconActiveColor: 'text-white',
    textActiveClass: 'text-indigo-700',
    descActiveClass: 'text-indigo-600'
  },
  BANK_TRANSFER: {
    key: 'BANK_TRANSFER',
    icon: 'transfer',
    label: '大额转账',
    activeClass: 'border-emerald-500 bg-gradient-to-br from-emerald-50 to-teal-50 shadow-lg shadow-emerald-500/25 ring-2 ring-emerald-500/20',
    inactiveClass: 'border-slate-200 bg-white hover:border-emerald-300 hover:shadow-md',
    iconActiveBg: 'bg-gradient-to-br from-emerald-500 to-teal-500 shadow-lg shadow-emerald-500/30',
    iconInactiveBg: 'bg-slate-100 group-hover:bg-emerald-100',
    iconActiveColor: 'text-white',
    textActiveClass: 'text-emerald-700',
    descActiveClass: 'text-emerald-600'
  }
}
const scanMethodMeta = {
  WECHAT: {
    icon: 'wechat',
    label: '微信扫码支付',
    hint: '适合在 PC 端展示二维码，由微信扫一扫完成付款。',
    actionLabel: '生成微信付款二维码'
  },
  ALIPAY: {
    icon: 'alipay',
    label: '支付宝扫码支付',
    hint: '适合在 PC 端展示二维码，由支付宝扫一扫完成付款。',
    actionLabel: '生成支付宝付款二维码'
  }
}

const scanData = ref(null)
const ebankData = ref(null)
const transferData = ref(null)
const allowedMethods = computed(() => {
  const raw = presentation.value?.allowedMethods
  if (!Array.isArray(raw) || raw.length === 0) {
    return ['SCAN_QR', 'EBANK', 'BANK_TRANSFER']
  }
  return raw
})
const allowScan = computed(() => allowedMethods.value.includes('SCAN_QR'))
const allowEbank = computed(() => allowedMethods.value.includes('EBANK'))
const allowTransfer = computed(() => allowedMethods.value.includes('BANK_TRANSFER'))
const paymentMethodCards = computed(() => {
  const cards = []
  if (allowScan.value) {
    cards.push(paymentMethodMeta.WECHAT, paymentMethodMeta.ALIPAY)
  }
  if (allowEbank.value) {
    cards.push(paymentMethodMeta.EBANK)
  }
  if (allowTransfer.value) {
    cards.push(paymentMethodMeta.BANK_TRANSFER)
  }
  return cards
})
const availableActiveMethods = computed(() => paymentMethodCards.value.map(item => item.key))
const currentScanMethod = computed(() => scanMethodMeta[activeMethod.value] || null)
const selectedEbankBank = computed(() => {
  return ebankOptions.find((item) => item.value === selectedEbankBankCode.value) || null
})
const successMethodLabel = computed(() => {
  const method = presentation.value?.successMethod || presentation.value?.paymentMethod || ''
  const channel = presentation.value?.successChannel || ''
  if (method === 'SCAN_QR' && channel === 'WECHAT') return '微信支付'
  if (method === 'SCAN_QR' && channel === 'ALIPAY') return '支付宝'
  if (method === 'EBANK') return '网银支付'
  if (method === 'BANK_TRANSFER') return '大额转账'
  if (method === 'SCAN_QR') return '扫码支付'
  return method || '-'
})

const statusLabel = computed(() => {
  const map = { 0: '待支付', 1: '支付中', 2: '支付成功', 3: '已关闭', 4: '支付凭证失效' }
  return map[statusCode.value] || '待支付'
})

const statusClass = computed(() => {
  const map = {
    0: 'bg-slate-100 text-slate-700',
    1: 'bg-amber-100 text-amber-700',
    2: 'bg-emerald-100 text-emerald-700',
    3: 'bg-red-100 text-red-700',
    4: 'bg-amber-100 text-amber-700'
  }
  return map[statusCode.value] || map[0]
})

const isTerminal = computed(() => [2, 3].includes(statusCode.value))
const isScanMethod = (method) => ['WECHAT', 'ALIPAY'].includes(method)
const scanExpired = computed(() => statusCode.value === 4 && !!scanData.value && isScanMethod(activeMethod.value))

const scanPayUrl = computed(() => {
  return scanData.value?.qrCode || ''
})
const scanQrImage = ref('')

const currencyText = (value) => {
  const n = Number(value)
  if (!Number.isFinite(n)) return '¥0.00'
  return `¥${n.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

const datetimeText = (value) => {
  if (!value) return '-'
  return String(value).replace('T', ' ')
}

const copyScanPayUrl = async () => {
  if (!scanPayUrl.value) return
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(scanPayUrl.value)
      ElMessage.success('支付链接已复制')
      return
    }
    throw new Error('clipboard unavailable')
  } catch (error) {
    ElMessage.warning('当前环境不支持自动复制，请手动复制支付链接')
  }
}

const copyTransferAccountNo = async () => {
  const raw = transferData.value?.accountNo
  if (!raw) return
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(raw)
      ElMessage.success('收款账号已复制')
      return
    }
    throw new Error('clipboard unavailable')
  } catch (error) {
    ElMessage.warning('当前环境不支持自动复制，请手动复制收款账号')
  }
}

const loadPresentation = async () => {
  loading.value = true
  loadError.value = ''
  try {
    const data = await getCashierPresentation(cashierNo.value, clientType)
    presentation.value = data || {}
    orderInfo.value = data?.orderInfo || {}
    statusCode.value = Number(data?.status ?? 0)
    ensureActiveMethodAvailable()
    if (!selectedEbankBankCode.value && ebankOptions.length > 0) {
      selectedEbankBankCode.value = ebankOptions[0].value
    }
  } catch (error) {
    loadError.value = error?.message || '无法加载收银台信息'
  } finally {
    loading.value = false
  }
}

const activateMethod = (method) => {
  if (!availableActiveMethods.value.includes(method)) return
  activeMethod.value = method
}

const ensureActiveMethodAvailable = () => {
  if (availableActiveMethods.value.includes(activeMethod.value)) {
    return
  }
  activeMethod.value = availableActiveMethods.value[0] || ''
}

const clearMethodPayloads = () => {
  scanData.value = null
  ebankData.value = null
  transferData.value = null
}

const startPolling = () => {
  stopPolling()
  if (!attemptNo.value || isTerminal.value) return
  pollTimer.value = setInterval(async () => {
    try {
      const data = await getCashierStatus(cashierNo.value, attemptNo.value)
      statusCode.value = Number(data?.status ?? statusCode.value)
      presentation.value = {
        ...presentation.value,
        failureReason: data?.failureReason || presentation.value?.failureReason || '',
        voucherExpiresAt: data?.voucherExpiresAt || presentation.value?.voucherExpiresAt || '',
        attemptStatus: data?.attemptStatus || presentation.value?.attemptStatus || '',
        orderStatus: data?.orderStatus || presentation.value?.orderStatus || ''
      }
      if ([2, 3, 4].includes(statusCode.value)) {
        stopPolling()
      }
    } catch (error) {
      // keep polling silently
    }
  }, 3000)
}

const stopPolling = () => {
  if (pollTimer.value) {
    clearInterval(pollTimer.value)
    pollTimer.value = null
  }
}

const createScanPay = async (walletType) => {
  if (isTerminal.value || !allowScan.value || !scanMethodMeta[walletType]) return
  actionLoading.value = true
  clearMethodPayloads()
  try {
    const data = await createCashierScan(cashierNo.value, {
      walletType,
      clientType
    })
    scanData.value = data
    attemptNo.value = data?.attemptNo || ''
    statusCode.value = 1
    startPolling()
  } catch (error) {
    ElMessage.error(error?.message || `${scanMethodMeta[walletType].label}失败`)
  } finally {
    actionLoading.value = false
  }
}

const refreshCurrentScan = async () => {
  if (!isScanMethod(activeMethod.value)) return
  await createScanPay(activeMethod.value)
}

const autoCreateDefaultScan = async () => {
  if (loading.value || loadError.value || isTerminal.value || !allowScan.value || !isScanMethod(activeMethod.value)) return
  if (scanData.value && !scanExpired.value) return
  await createScanPay(activeMethod.value)
}

const createEbankPay = async () => {
  if (isTerminal.value || !allowEbank.value || !selectedEbankBankCode.value) return
  actionLoading.value = true
  clearMethodPayloads()
  try {
    const data = await createCashierEbank(cashierNo.value, {
      bankCode: selectedEbankBankCode.value,
      accountType: selectedEbankAccountType.value,
      clientType
    })
    ebankData.value = data
    attemptNo.value = data?.attemptNo || ''
    statusCode.value = 1
    startPolling()
  } catch (error) {
    ElMessage.error(error?.message || '发起网银支付失败')
  } finally {
    actionLoading.value = false
  }
}

const createTransferPay = async () => {
  if (isTerminal.value || !allowTransfer.value) return
  actionLoading.value = true
  clearMethodPayloads()
  try {
    const data = await createCashierTransfer(cashierNo.value, { clientType })
    transferData.value = data
    attemptNo.value = data?.attemptNo || ''
    statusCode.value = 1
    startPolling()
  } catch (error) {
    ElMessage.error(error?.message || '获取转账信息失败')
  } finally {
    actionLoading.value = false
  }
}

watch(
  scanPayUrl,
  async (raw) => {
    if (!raw) {
      scanQrImage.value = ''
      return
    }
    try {
      scanQrImage.value = await QRCode.toDataURL(raw, {
        width: 320,
        margin: 1,
        errorCorrectionLevel: 'M',
        color: {
          dark: '#0f172a',
          light: '#ffffff'
        }
      })
    } catch (error) {
      scanQrImage.value = ''
      ElMessage.warning('二维码生成失败，请复制支付链接继续支付')
    }
  },
  { immediate: true }
)

watch(availableActiveMethods, ensureActiveMethodAvailable, { immediate: true })

watch(activeMethod, async (nextMethod, previousMethod) => {
  if (loading.value || nextMethod === previousMethod) {
    return
  }
  if (!isScanMethod(nextMethod)) {
    return
  }
  await createScanPay(nextMethod)
})

onMounted(async () => {
  await loadPresentation()
  await autoCreateDefaultScan()
})
onBeforeUnmount(stopPolling)
</script>

<style scoped>
.wallet-btn {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border-radius: 0.75rem;
  border: 2px solid #e2e8f0;
  background: #fff;
  padding: 0.875rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #334155;
  transition: all 0.2s ease;
}
.wallet-btn:hover:not(:disabled) {
  border-color: #93c5fd;
  background: #eff6ff50;
}
.wallet-btn.is-active {
  border-color: #3b82f6;
  background: #eff6ff80;
  color: #1d4ed8;
  box-shadow: 0 4px 12px rgb(59 130 246 / 0.15);
}
.wallet-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.action-btn {
  position: relative;
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border-radius: 0.875rem;
  border: none;
  padding: 0.875rem 1.25rem;
  font-size: 0.9375rem;
  font-weight: 700;
  color: #fff;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  background: linear-gradient(135deg, #2563eb 0%, #3b82f6 50%, #0ea5e9 100%);
  box-shadow: 0 4px 14px rgb(37 99 235 / 0.4), 0 0 0 1px rgb(37 99 235 / 0.1);
}
.action-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgb(37 99 235 / 0.45), 0 0 0 1px rgb(37 99 235 / 0.15);
}
.action-btn:active:not(:disabled) {
  transform: translateY(0);
}
.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}
</style>



